import React, { useCallback, useEffect } from 'react';
import * as Routes from 'modules/routes';
import { Input } from '@/components/ui/Form';
import cn from '@/utils/cn';
import { useForm } from 'react-hook-form';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { IAISuggestion, IQuestion, IQuestionAnswer } from '@/components/staffer/Grading/types';
import { useDebounceCallback } from 'usehooks-ts';
import Axios from 'axios';
import useGradingStore from '@/components/staffer/Grading/useGradingStore';
import useFlags from '@/hooks/useFlags';
import Tooltip from '@/components/common/Tooltip';
import { formatLocalTimestamp } from '@/components/common/Utils';
import I18n from '@/modules/i18n';
import { denormalizedScore, normalizedScore } from '@/components/staffer/Grading/utils';
import AISuggestionReview from '@/components/staffer/Grading/AISuggestion/AISuggestionReview';
import { displayedSuggestedScore, REVIEWABLE_STATES } from '@/components/staffer/Grading/AISuggestion/aiSuggestion';
import checkIcon from '../../../../images/icons/check-thin.svg';
import timesIcon from '../../../../images/icons/times.svg';

const ScoreInput = ({ attrName, disabled, hasErrors, formRegister, isTeacher, isUnsubmittedAssessment }) => (
  <Tooltip
    content="This question is automatically graded"
    shouldDisplay={isTeacher && disabled && !isUnsubmittedAssessment}
  >
    <Input
      className="tw-text-center tw-p-0.5 tw-h-8 disabled:tw-bg-light-grey"
      containerClasses={cn('tw-mx-1 tw-w-8 tw-h-8 tw-p-0', {
        'tw-border-red tw-border-[1px] tw-border-solid tw-rounded': hasErrors,
      })}
      disabled={disabled}
      name={attrName}
      step="0.1"
      type="number"
      {...formRegister}
    />
  </Tooltip>
);

interface SaveStatusProps {
  errorMessage?: string;
  gradingTeacher?: string;
  isSaving: boolean;
  justSaved: boolean;
  scoreUpdatedAt?: string;
}

// AIDEV-NOTE: always renders, so the row keeps its height and a previously saved timestamp stays
// visible. justSaved reports the time alone; scoreUpdatedAt is from an earlier session, so it
// carries the date too.
export const SaveStatus = ({
  errorMessage, gradingTeacher, isSaving, justSaved, scoreUpdatedAt,
}: SaveStatusProps) => {
  const savedAt = justSaved
    ? formatLocalTimestamp(new Date(), true)
    : formatLocalTimestamp(scoreUpdatedAt, false);
  let message = '';

  if (isSaving) {
    message = `${I18n.t('saving')}…`;
  } else if (savedAt && gradingTeacher) {
    message = I18n.t('last_updated_on_by', { teacher: gradingTeacher, time: savedAt });
  } else if (savedAt) {
    message = I18n.t('last_updated_on', { time: savedAt });
  }

  return (
    <p
      className={cn('tw-inline-block tw-text-sm tw-min-h-5', errorMessage ? 'tw-text-red' : 'tw-text-gray-700')}
      role="status"
    >
      {errorMessage || message}
    </p>
  );
};

interface ScoreEditProps {
  aiSuggestion?: IAISuggestion;
  isUnsubmittedAssessment?: boolean;
  question: IQuestion;
  questionAnswer?: IQuestionAnswer;
  studentId: number | string;
}

const ScoreEdit = ({ aiSuggestion, isUnsubmittedAssessment, question, questionAnswer, studentId }: ScoreEditProps) => {
  const { assignment, classroom, isTeacher, submission, updateQuestionAnswer } = useGradingStore();
  const { coTeacherAssignmentVisibility: showsGrader } = useFlags();

  const client = useQueryClient();

  const getFormattedScore = val => (
    (!Number.isNaN(val) && val % 1 === 0) ? Math.floor(val) : val
  );

  // AIDEV-NOTE: PLAT-18100 — an ungraded question must leave the box empty, never 0: a null score
  // (or no answer row at all, for a question the student never reached) is indistinguishable from an
  // earned zero once normalizedScore coerces it with `score || 0`. Test score for null explicitly so
  // a genuine 0 still renders.
  const scoreInputValue = () => {
    if (questionAnswer?.score == null) return '';

    const parsedScore = parseFloat(questionAnswer.score);
    if (Number.isNaN(parsedScore)) return '';

    return normalizedScore(assignment, getFormattedScore(parsedScore));
  };

  const {
    formState: { errors },
    getFieldState,
    handleSubmit,
    register,
    setFocus,
    setValue,
    trigger,
  } = useForm({
    defaultValues: { scoreInput: scoreInputValue() as string | number },
  });

  // Update form value when questionAnswer prop changes
  useEffect(() => {
    setValue('scoreInput', scoreInputValue());
  }, [question.id, questionAnswer?.score, setValue]);

  const saveMutation = useMutation({
    mutationFn: (data: { acceptAISuggestion?: boolean, scoreInput: string | number }) => Axios.post(
      Routes.plato_api_grade_question_answer_path(studentId, {
        accept_ai_suggestion: data.acceptAISuggestion || undefined,
        assignment_id: assignment.id,
        assignment_type: assignment.assignment_details.exercise_type,
        question_answer_id: questionAnswer?.id,
        question_id: question.id,
        score: denormalizedScore(
          assignment,
          typeof data.scoreInput === 'string' ? parseFloat(data.scoreInput) : data.scoreInput,
        ),
      }),
    ),
    mutationKey: ['gradeQuestionAnswer', studentId, question.id],
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error('Error updating score:', error);
    },
    onSuccess: async (res) => {
      await client.invalidateQueries(['submission', assignment.id.toString(), studentId.toString()]);
      await client.invalidateQueries(['studentAnswer', submission?.user_id]);
      await client.invalidateQueries(['studentAnswer', classroom?.id]);

      const { data } = res;
      if (data.success) updateQuestionAnswer(data);
    },
  });

  const rejectMutation = useMutation({
    mutationFn: () => Axios.post(Routes.plato_api_reject_ai_suggestion_question_answer_path(questionAnswer.id)),
    onError: () => client.invalidateQueries(['studentAnswer']),
    onSuccess: () => {
      setFocus('scoreInput');
      return client.invalidateQueries(['studentAnswer']);
    },
  });

  // Reset mutation state when question or student changes
  useEffect(() => {
    saveMutation.reset();
    rejectMutation.reset();
  }, [question.id, studentId]);

  const handleSave = (data: { acceptAISuggestion?: boolean, scoreInput: string | number }) => {
    rejectMutation.reset();
    saveMutation.mutate(data);
  };

  const debouncedSave = useCallback(useDebounceCallback(handleSave, 1000), [question.id]);

  const handleAccept = () => {
    debouncedSave.cancel();
    handleSave({
      acceptAISuggestion: true,
      scoreInput: displayedSuggestedScore(assignment, aiSuggestion.suggested_score),
    });
  };

  const handleInputChange = async (attrName) => {
    const isValid = await trigger(attrName);
    if (isValid) handleSubmit(debouncedSave)();
    else debouncedSave.cancel();
  };

  const formRegister = attrName => (
    register(attrName, {
      max: { message: 'Value cannot exceed max points', value: normalizedScore(assignment, question.points) },
      min: { message: 'Value cannot be less than 0', value: 0 },
      onChange: () => handleInputChange(attrName),
      required: true,
    })
  );

  const giveCreditButton = (fullCredit: boolean) => {
    const message = fullCredit ? 'Give Full Credit' : 'Give No Credit';
    return (
      <Tooltip content={message} size="medium" theme="white">
        <button
          className="tw-ml-1 tw-px-2 tw-py-2 tw-rounded tw-border tw-border-solid tw-border-[#ccc] tw-bg-transparent
           tw-cursor-pointer tw-w-9 tw-h-8 tw-flex tw-justify-center tw-items-center"
          type="button"
          onClick={() => {
            setValue('scoreInput', fullCredit ? normalizedScore(assignment, question.points) : 0, {
              shouldDirty: true,
              shouldTouch: true,
              shouldValidate: true,
            });
            handleInputChange('scoreInput');
          }}
        >
          <img alt={message} className="tw-w-8 tw-h-8" src={fullCredit ? checkIcon : timesIcon} />
        </button>
      </Tooltip>
    );
  };

  const errorMessage = getFieldState('scoreInput').error?.message
    || (saveMutation.isError || rejectMutation.isError ? 'Save failed' : undefined);
  const showsReview = REVIEWABLE_STATES.includes(aiSuggestion?.state);
  const hidesQuickGradeButtons = ['ai_suggested_pending', 'ai_accepted_as_is'].includes(aiSuggestion?.state);

  return (
    <div className="tw-w-full tw-py-4">
      <div className="tw-flex tw-justify-end tw-items-center">
        {showsReview && (
          <AISuggestionReview
            aiSuggestion={aiSuggestion}
            isBusy={saveMutation.isLoading || rejectMutation.isLoading}
            onAccept={handleAccept}
            onReject={() => {
              saveMutation.reset();
              rejectMutation.mutate();
            }}
          />
        )}
        <strong>Score:</strong>
        {(isTeacher && !question.auto_gradable && !isUnsubmittedAssessment && !hidesQuickGradeButtons) && (
          <div className="tw-mr-1 tw-flex">
            {giveCreditButton(true)}
            {giveCreditButton(false)}
          </div>
        )}
        <ScoreInput
          attrName="scoreInput"
          disabled={!isTeacher || question.auto_gradable || isUnsubmittedAssessment}
          formRegister={formRegister('scoreInput')}
          hasErrors={Object.keys(errors).includes('scoreInput') || getFieldState('scoreInput').error}
          isTeacher={isTeacher}
          isUnsubmittedAssessment={isUnsubmittedAssessment}
        />
        /
        <div>{normalizedScore(assignment, question.points)}</div>
      </div>
      <div className="tw-flex tw-justify-end tw-mt-1">
        <div className="tw-flex tw-flex-col">
          <SaveStatus
            errorMessage={errorMessage}
            gradingTeacher={showsGrader ? questionAnswer?.grading_teacher : undefined}
            isSaving={saveMutation.isLoading}
            justSaved={saveMutation.isSuccess}
            scoreUpdatedAt={questionAnswer?.score_updated_at}
          />
        </div>
      </div>
    </div>
  );
};

export default ScoreEdit;
